import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OVERRIDE_ERROR_MESSAGE, CUSTOM_ERROR_HANDLING } from '@shared/http';
import {
  DocumentTaskCreatedPayload,
  MessagePayload,
  MessageReadState,
  MessageResponse,
  QuestionTaskCreatedPayload,
  SectionTaskCreatedPayload,
  TAssignTaskRequest,
  TBulkUpdateTaskStatusRequest,
  TCreateQuestionTaskRequest,
  TCreateSectionTaskRequest,
  TSearchTaskInstanceRequest,
  TTaskInstanceDetailResponse,
  TTaskInstanceDropdownResponse,
  TTaskListDropdownQueryParams,
  TUpdateTaskStatusRequest,
  TaskInstance,
  TaskInstanceDetail,
  ThreadMessage,
  WorkspaceMessagesPage,
} from '../types';

import {
  URL_SPECIAL_CHARACTER_PATTERN,
  WORKSPACE_URLS,
} from '@data-access/shared';
import { COLLAB_HUB_MESSAGE_TASKS_ERRORS } from '../constants';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private readonly _http = inject(HttpClient);

  /*
   * Fetches a list of task instances for a given workspace.
   * @param workspaceId - The ID of the workspace to fetch tasks from.
   * @param params - Optional parameters to filter the task list.
   */
  getTaskList(workspaceId: string, data: TSearchTaskInstanceRequest) {
    return this._http.post<TaskInstance[]>(
      `${WORKSPACE_URLS?.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /*
   * Fetches a list of task instances for a given taskIds
   * */
  getTaskListByIds(workspaceId: string, data: TSearchTaskInstanceRequest) {
    return this._http.post<TaskInstance[]>(
      `${WORKSPACE_URLS?.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /**
   * Fetches a list of task instances for a given workspace as dropdown items.
   * @param workspaceId - The ID of the workspace to fetch tasks from.
   * @param params - Parameters to filter the task list.
   * @returns An observable that emits the task instance dropdown items.
   */
  getTaskListAsDropdownItems(
    workspaceId: string,
    data: TTaskListDropdownQueryParams,
  ) {
    return this._http.post<TTaskInstanceDropdownResponse[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/`,
      data,
    );
  }

  /*
   * Fetches the details of a specific task instance by its ID.
   * @param {workspaceId} - The ID of the workspace containing the task.
   * @param {taskInstanceId} - The ID of the task instance to retrieve.
   * @return An observable that emits the task instance details.
   */
  getTaskById(workspaceId: string, taskInstanceId: string) {
    return this._http.get<TaskInstanceDetail>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/`,
    );
  }

  /*
   ** Assigns a task to a specific user.
   * @param {string} workspaceId - The ID of the workspace containing the task.
   * @param {string} taskInstanceId - The ID of the task instance to be assigned.
   * @param {string} assigneeId - The ID of the user to whom the task will be assigned.
   * @return An observable that emits the server response.
   **/
  assignTask(
    workspaceId: string,
    taskInstanceId: string,
    request: TAssignTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/assign/`,
      request,
    );
  }

  /*
   * Updates the status of a specific task instance.
   * @param {string}
   * workspaceId - The ID of the workspace containing the task.
   *   @param {string} taskInstanceId - The ID of the task instance to be updated.
   *   @param {TaskStatus} status - The new status to set for the task instance.
   *   @return An observable that emits the updated task instance.
   */
  updateTaskStatus(
    workspaceId: string,
    taskInstanceId: string,
    request: TUpdateTaskStatusRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/${taskInstanceId}/status/`,
      request,
    );
  }

  bulkUpdateTaskStatus(
    workspaceId: string,
    request: TBulkUpdateTaskStatusRequest,
  ) {
    return this._http.put<string[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/`,
      request,
    );
  }

  /**
   * Creates a task for a section in a workspace.
   * @param workspaceId - The ID of the workspace to create the task for.
   * @param request - The request body containing the section ID and task data.
   * @returns An observable that emits the created task instance.
   */
  createTaskForSection(
    workspaceId: string,
    request: TCreateSectionTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/section/`,
      request,
    );
  }

  /**
   * Creates a task for a question in a workspace.
   * @param workspaceId - The ID of the workspace to create the task for.
   * @param request - The request body containing the question ID and task data.
   * @returns An observable that emits the created task instance.
   */
  createTaskForQuestion(
    workspaceId: string,
    request: TCreateQuestionTaskRequest,
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      request,
    );
  }

  /**
   * Retrieves messages for a specific thread in a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread to retrieve messages from.
   * @returns An observable that emits the thread messages.
   */
  getThreadMessages(workspaceId: string, threadId: string) {
    return this._http.get<ThreadMessage[]>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/${threadId}/`,
    );
  }

  /**
   * ERM-13545 — one keyset page of the submission-level unified thread,
   * newest first, across every task thread the viewer may see.
   * @param workspaceId - The ID of the workspace to page over.
   * @param limit - Page size (the backend caps it at 100).
   * @param before - A previous page's `next` value, echoed back verbatim,
   * to fetch the strictly-older page. Omit for the newest page.
   * @returns An observable that emits the page plus its `next` cursor.
   */
  getWorkspaceMessages(workspaceId: string, limit: number, before?: string) {
    return this._http.get<WorkspaceMessagesPage>(
      `${WORKSPACE_URLS.V2}${workspaceId}/messages/`,
      { params: { limit, ...(before ? { before } : {}) } },
    );
  }

  /**
   * Records that the viewer has read the unified thread up to a message (ERM-12589).
   * The server keeps one read position per user and workspace and only ever moves
   * it forward, so marking an older message than the current position is a no-op.
   * @param workspaceId - The workspace whose thread was read.
   * @param messageId - The newest message the viewer has seen.
   * @returns An observable that emits the read state after the advance.
   */
  markWorkspaceMessagesRead(workspaceId: string, messageId: string) {
    return this._http.put<MessageReadState>(
      `${WORKSPACE_URLS.V2}${workspaceId}/messages/read-marker/`,
      { message_id: messageId },
    );
  }

  /**
   * Creates a new message in a specific thread within a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param taskInstanceId - The ID of the task instance associated with the thread.
   * @param message - The message content and metadata to be created.
   * @returns An observable that emits the created thread message.
   */
  createThreadMessage(
    workspaceId: string,
    taskInstanceId: string,
    message: MessagePayload,
  ) {
    return this._http.post<MessageResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/${taskInstanceId}/message/`,
      message,
    );
  }

  /**
   * Creates a message on the submission-level thread, which hangs off no to-do
   * (ERM-14098). The task-scoped sibling above needs a task instance in its
   * path, so a sender with no open to-do had no endpoint to call at all.
   * @param workspaceId - The ID of the workspace to post the message on.
   * @param message - The message content and metadata to be created.
   * @returns An observable that emits the created thread message.
   */
  createWorkspaceMessage(workspaceId: string, message: MessagePayload) {
    const _messageContainsUrlWithSpecialChars =
      'rich_text' in message.data &&
      URL_SPECIAL_CHARACTER_PATTERN.test(`${message.data.rich_text ?? ''}`);
    return this._http.post<MessageResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/message/`,
      message,
      {
        context: new HttpContext().set(
          OVERRIDE_ERROR_MESSAGE,
          _messageContainsUrlWithSpecialChars
            ? COLLAB_HUB_MESSAGE_TASKS_ERRORS.AZURE_403_SERVER_ERROR_SPECIAL_CHARS
            : null,
        ),
      },
    );
  }

  /**
   * Soft-deletes a message in a workspace thread.
   * The backend retains the record for audit, but the Messages tab should
   * no longer surface it once this call succeeds.
   */
  deleteWorkspaceMessage(workspaceId: string, messageId: string) {
    return this._http.delete<MessageResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/message/${messageId}/delete/`,
    );
  }

  /**
   * Retrieves the thread ID associated with a specific task instance in a workspace.
   * @param workspaceId - The ID of the workspace containing the task.
   * @param taskInstanceId - The ID of the task instance to retrieve the thread for.
   * @returns An observable that emits the thread ID.
   */
  getThreadForTaskInstance(workspaceId: string, taskInstanceId: string) {
    return this._http.get<{ thread_id: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/tasks/${taskInstanceId}/thread/`,
    );
  }

  /**
   * Creates a document-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and document ID.
   * @returns An observable that emits the created task instance details.
   */
  createDocumentTask(workspaceId: string, request: DocumentTaskCreatedPayload) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/document/`,
      this._encodeDataPayload(request),
    );
  }

  /**
   * Creates a question-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and question ID.
   * @param options - `silentErrors` skips the global error snackbar so the caller can surface its own message (ERM-13220 composer).
   * @returns An observable that emits the created task instance details.
   */
  createQuestionTask(
    workspaceId: string,
    request: QuestionTaskCreatedPayload,
    options?: { silentErrors?: boolean },
  ) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      this._encodeDataPayload(request),
      options?.silentErrors
        ? {
            context: new HttpContext().set(
              CUSTOM_ERROR_HANDLING,
              'silent-mode',
            ),
          }
        : undefined,
    );
  }

  /**
   * Creates a section-related task in a specified workspace.
   * @param workspaceId - The ID of the workspace where the task will be created.
   * @param request - The request body containing task details such as title, description, assignee, and question ID.
   * @returns An observable that emits the created task instance details.
   */
  createSectionTask(workspaceId: string, request: SectionTaskCreatedPayload) {
    return this._http.post<TTaskInstanceDetailResponse>(
      `${WORKSPACE_URLS.V2}${workspaceId}/task/question/`,
      request,
    );
  }

  /**
   * Exports the messages of a specific thread in a workspace.
   * @param workspaceId - The ID of the workspace containing the thread.
   * @param threadId - The ID of the thread to export messages from.
   * @returns An observable that emits the exported messages as a Blob.
   */
  exportThreadMessages(workspaceId: string, threadId: string) {
    return this._http.get<Blob>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/${threadId}/export/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * Exports all threads in a workspace.
   * @param workspaceId - The ID of the workspace to export threads from.
   * @returns An observable that emits the exported threads as a Blob.
   */
  exportAllThreads(workspaceId: string) {
    return this._http.get<Blob>(
      `${WORKSPACE_URLS.V2}${workspaceId}/threads/export/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * Sends a manual notification email to all workspace recipients.
   * @param workspaceId - The ID of the workspace to send notifications for.
   * @returns An observable that emits the server response.
   */
  sendManualNotification(workspaceId: string) {
    return this._http.post<{ message: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/notify/`,
      {},
    );
  }

  /**
   * ERM-12314 — Notify Customer for the redesigned Collab Hub drawer.
   *
   * Deliberately not `notify/`: that legacy endpoint emails every
   * assignment, this one targets external (customer) assignments and
   * includes the open to-do count.
   */
  notifyCustomer(workspaceId: string) {
    return this._http.post<{ message: string }>(
      `${WORKSPACE_URLS.V2}${workspaceId}/notify-customer/`,
      {},
    );
  }

  private _encodeDataPayload(
    payload:
      | SectionTaskCreatedPayload
      | QuestionTaskCreatedPayload
      | DocumentTaskCreatedPayload,
  ) {
    try {
      // Convert the string to UTF-8 bytes first to handle Unicode characters
      const jsonString = JSON.stringify(payload);
      const utf8Bytes = new TextEncoder().encode(jsonString);
      // Convert bytes to binary string that btoa can handle
      const binaryString = Array.from(utf8Bytes)
        .map((byte) => String.fromCharCode(byte))
        .join('');
      // Encode to base64
      return {
        data: btoa(binaryString),
      };
    } catch (e) {
      // Fallback to original method if TextEncoder is not available
      console.error(`Error occurred during encoding payload: ${e}`);
      return {
        data: btoa(JSON.stringify(payload)),
      };
    }
  }
}
